import { useState, useEffect, useCallback } from "react";
import { listCargoAgencies, createCargoAgency, updateCargoAgency, deleteCargoAgency } from "@/lib/api";
import { Building2, Plus, Pencil, Trash2, Mail, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const empty = { name: "", type: "agency", address: "", city: "", country: "", tel: "", email: "", sita: "", notes: "" };

export default function CargoAgencyPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listCargoAgencies();
      setItems(res.data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (it) => { setForm({ ...empty, ...it }); setEditId(it.id); setOpen(true); };

  const save = async () => {
    if (!form.name) { toast.error("Agency name is required"); return; }
    try {
      if (editId) { await updateCargoAgency(editId, form); toast.success("Agency updated"); }
      else { await createCargoAgency(form); toast.success("Agency created"); }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save agency"); }
  };

  const confirmDelete = async () => {
    try { await deleteCargoAgency(deleteId); toast.success("Agency deleted"); setDeleteId(null); load(); }
    catch { toast.error("Failed to delete"); }
  };

  const FIELDS = [
    { key: "address", label: "Address" }, { key: "city", label: "City" },
    { key: "country", label: "Country" }, { key: "tel", label: "Telephone" },
    { key: "email", label: "Email" }, { key: "sita", label: "SITA Address" },
  ];

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="cargo-agency-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Building2 className="text-emerald-500" /> Agency
          </h1>
          <p className="text-slate-500 text-sm mt-1">Cargo agents & partners address book.</p>
        </div>
        <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="new-agency-btn">
          <Plus size={18} className="mr-2" /> New Agency
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <p className="text-slate-400 text-sm">Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-slate-400 text-sm">No agencies yet.</p>
        ) : items.map((it) => (
          <div key={it.id} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid={`agency-card-${it.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-slate-900">{it.name}</h3>
                <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${(it.type || "agency") === "airline" ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"}`} data-testid={`agency-type-${it.id}`}>
                  {(it.type || "agency") === "airline" ? "Airline" : "Agency"}
                </span>
              </div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" onClick={() => openEdit(it)} className="h-8 w-8 text-slate-500 hover:text-emerald-600" data-testid={`edit-agency-${it.id}`}><Pencil size={15} /></Button>
                <Button size="icon" variant="ghost" onClick={() => setDeleteId(it.id)} className="h-8 w-8 text-slate-500 hover:text-red-600" data-testid={`delete-agency-${it.id}`}><Trash2 size={15} /></Button>
              </div>
            </div>
            <div className="mt-2 space-y-1 text-sm text-slate-600">
              {(it.address || it.city || it.country) && <p>{[it.address, it.city, it.country].filter(Boolean).join(", ")}</p>}
              {it.tel && <p className="flex items-center gap-2"><Phone size={14} className="text-slate-400" /> {it.tel}</p>}
              {it.email && <p className="flex items-center gap-2"><Mail size={14} className="text-slate-400" /> {it.email}</p>}
              {it.sita && <p className="text-xs text-slate-400">SITA: {it.sita}</p>}
              {it.notes && <p className="text-xs text-slate-500 mt-1">{it.notes}</p>}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="agency-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit Agency" : "New Agency"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
            <div className="sm:col-span-2">
              <Label className="text-xs text-slate-500">Name *</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1" data-testid="agency-name-input" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs text-slate-500">Type</Label>
              <div className="mt-1 grid grid-cols-2 gap-2" data-testid="agency-type-toggle">
                {[["agency", "Agency"], ["airline", "Airline"]].map(([val, lbl]) => (
                  <button key={val} type="button" onClick={() => set("type", val)}
                    className={`rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${(form.type || "agency") === val ? (val === "airline" ? "border-blue-500 bg-blue-50 text-blue-700" : "border-emerald-500 bg-emerald-50 text-emerald-700") : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
                    data-testid={`agency-type-${val}`}>{lbl}</button>
                ))}
              </div>
            </div>
            {FIELDS.map((f) => (
              <div key={f.key} className={f.key === "address" ? "sm:col-span-2" : ""}>
                <Label className="text-xs text-slate-500">{f.label}</Label>
                <Input value={form[f.key]} onChange={(e) => set(f.key, e.target.value)} className="mt-1" data-testid={`agency-${f.key}-input`} />
              </div>
            ))}
            <div className="sm:col-span-2">
              <Label className="text-xs text-slate-500">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1" rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-agency-btn">{editId ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this agency?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-agency-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
